import React, { useState, useEffect } from 'react';
import './ComparePage.css';
import DragDropArea from './DragDropArea';
import FileSummary from './FileSummary';
import FrameQC from './FrameQC';
import CutScan from './CutScan';
import CompareWarnings, { buildCompareWarnings } from './CompareWarnings';
import MetadataDiff from './MetadataDiff';
import { extractMetadata } from '../utils/metadataExtractor';
import { analyzeBlankFrames, extractTiming } from '../utils/blackFrameDetector';
import { ArrowRight } from 'lucide-react';

function ComparePage() {
    const [beforeFile, setBeforeFile] = useState(null);
    const [afterFile, setAfterFile] = useState(null);
    const [beforeMeta, setBeforeMeta] = useState(null);
    const [afterMeta, setAfterMeta] = useState(null);
    const [beforeUrl, setBeforeUrl] = useState(null);
    const [afterUrl, setAfterUrl] = useState(null);
    const [loadingBefore, setLoadingBefore] = useState(false);
    const [loadingAfter, setLoadingAfter] = useState(false);
    const [beforeQC, setBeforeQC] = useState(null);
    const [afterQC, setAfterQC] = useState(null);
    const [qcLoadingBefore, setQcLoadingBefore] = useState(false);
    const [qcLoadingAfter, setQcLoadingAfter] = useState(false);
    const [beforeCutResult, setBeforeCutResult] = useState(null);
    const [afterCutResult, setAfterCutResult] = useState(null);
    // null | 'same-file' (same file dropped twice) | 'identical-content' (byte-identical, different name)
    const [sameness, setSameness] = useState(null);

    useEffect(() => {
        if (beforeFile) {
            const url = URL.createObjectURL(beforeFile);
            setBeforeUrl(url);
            return () => URL.revokeObjectURL(url);
        } else { setBeforeUrl(null); }
    }, [beforeFile]);

    useEffect(() => {
        if (afterFile) {
            const url = URL.createObjectURL(afterFile);
            setAfterUrl(url);
            return () => URL.revokeObjectURL(url);
        } else { setAfterUrl(null); }
    }, [afterFile]);

    // Run the blank/black-frame check for one side (videos only). Kicked off
    // after metadata so the specs render immediately while frames decode.
    const runFrameQC = async (file, meta, setQC, setQcLoading) => {
        if (!file.type.startsWith('video/')) {
            setQC(null);
            return;
        }
        setQcLoading(true);
        try {
            const qc = await analyzeBlankFrames(file, extractTiming(meta));
            setQC(qc);
        } catch (e) {
            console.error('Frame QC failed:', e);
            setQC(null);
        } finally {
            setQcLoading(false);
        }
    };

    // Detect when both sides are the same file. Cheap path first: a matching
    // name/size/mtime/type signature means the same file was dropped twice.
    // Only when sizes match but names differ do we hash to catch a byte-identical
    // copy under a different name.
    useEffect(() => {
        if (!beforeFile || !afterFile) { setSameness(null); return; }
        if (sameSignature(beforeFile, afterFile)) { setSameness('same-file'); return; }
        if (beforeFile.size !== afterFile.size) { setSameness(null); return; }

        let cancelled = false;
        setSameness(null);
        (async () => {
            try {
                const [h1, h2] = await Promise.all([sha256(beforeFile), sha256(afterFile)]);
                if (!cancelled) setSameness(h1 && h1 === h2 ? 'identical-content' : null);
            } catch (e) {
                if (!cancelled) setSameness(null);
            }
        })();
        return () => { cancelled = true; };
    }, [beforeFile, afterFile]);

    const handleBefore = async (file) => {
        setBeforeFile(file);
        setLoadingBefore(true);
        setBeforeQC(null);
        let meta = null;
        try {
            meta = await extractMetadata(file);
            setBeforeMeta(meta);
        } catch (e) {
            console.error('Before extraction failed:', e);
        } finally {
            setLoadingBefore(false);
        }
        if (meta) runFrameQC(file, meta, setBeforeQC, setQcLoadingBefore);
    };

    const handleAfter = async (file) => {
        setAfterFile(file);
        setLoadingAfter(true);
        setAfterQC(null);
        let meta = null;
        try {
            meta = await extractMetadata(file);
            setAfterMeta(meta);
        } catch (e) {
            console.error('After extraction failed:', e);
        } finally {
            setLoadingAfter(false);
        }
        if (meta) runFrameQC(file, meta, setAfterQC, setQcLoadingAfter);
    };

    const hasBoth = beforeMeta && afterMeta;
    const warnings = buildCompareWarnings({
        sameness,
        beforeMeta, afterMeta,
        beforeQC, afterQC,
        beforeCut: beforeCutResult, afterCut: afterCutResult,
    });

    return (
        <div className="compare-layout">
            <CompareWarnings warnings={warnings} />

            {/* Before panel */}
            <div className="card compare-panel">
                <div className="compare-panel-header">
                    <span className="compare-label compare-label-before">BEFORE</span>
                    {beforeFile && <span className="compare-filename">{beforeFile.name}</span>}
                </div>
                <DragDropArea
                    onFileSelect={handleBefore}
                    isCompact={!!beforeFile}
                    previewUrl={beforeUrl}
                    fileType={beforeFile?.type}
                    fileName={beforeFile?.name}
                />
                {loadingBefore && (
                    <div className="compare-loading">
                        <div className="spinner spinner-sm"></div>
                        <span>Analyzing...</span>
                    </div>
                )}
                {beforeMeta && !loadingBefore && (
                    <>
                        <FileSummary metadata={beforeMeta} file={beforeFile} compareWith={afterMeta} />
                        <FrameQC result={beforeQC} loading={qcLoadingBefore} compareWith={afterQC} />
                        <CutScan file={beforeFile} compareResult={afterCutResult} onResult={setBeforeCutResult} />
                    </>
                )}
            </div>

            {/* After panel */}
            <div className="card compare-panel">
                <div className="compare-panel-header">
                    <span className="compare-label compare-label-after">AFTER</span>
                    {afterFile && <span className="compare-filename">{afterFile.name}</span>}
                </div>
                <DragDropArea
                    onFileSelect={handleAfter}
                    isCompact={!!afterFile}
                    previewUrl={afterUrl}
                    fileType={afterFile?.type}
                    fileName={afterFile?.name}
                />
                {loadingAfter && (
                    <div className="compare-loading">
                        <div className="spinner spinner-sm"></div>
                        <span>Analyzing...</span>
                    </div>
                )}
                {afterMeta && !loadingAfter && (
                    <>
                        <FileSummary metadata={afterMeta} file={afterFile} compareWith={beforeMeta} />
                        <FrameQC result={afterQC} loading={qcLoadingAfter} compareWith={beforeQC} />
                        <CutScan file={afterFile} compareResult={beforeCutResult} onResult={setAfterCutResult} />
                    </>
                )}
            </div>

            {/* Diff panel */}
            <div className="card compare-diff-panel">
                {hasBoth ? (
                    <MetadataDiff before={beforeMeta} after={afterMeta} />
                ) : (
                    <div className="compare-empty-diff">
                        <ArrowRight size={24} className="compare-empty-icon" />
                        <p className="compare-empty-title">Metadata Diff</p>
                        <p className="compare-empty-hint">
                            Drop a file on each side to compare metadata
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

/** Same file (name + size + mtime + type) dropped on both sides. */
function sameSignature(a, b) {
    return a.name === b.name
        && a.size === b.size
        && a.lastModified === b.lastModified
        && a.type === b.type;
}

/** SHA-256 hex digest of a file's bytes (secure-context only). */
async function sha256(file) {
    if (!window.crypto?.subtle) return null;
    const buf = await file.arrayBuffer();
    const digest = await window.crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export default ComparePage;
